import { useIsFocused } from "@react-navigation/native";
import { useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  FlatList,
  Image,
  Modal,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Dimensions,
  Platform,
  Linking,
  ActivityIndicator,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import PageTitle from "../../components/PageTitle";
import TextBoxIcon from "../../components/TextBoxIcon";
import Button from "../../components/Button";
import { GlobalContext } from "../../contexts/global";
import { getAllUserDocuments, getDocumentTypes, reviewDocument } from "../../services/Api/admin";
import { urlApi, getApiUrl } from "../../services/Api";
import { SecureStorage } from "@helpers/storage";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";

const PAGE_SIZE = 20;
const { width: SCREEN_WIDTH } = Dimensions.get("window");

const DOCUMENT_TYPE_ICONS = {
  CNH: "card-account-details",
  RG: "card-account-details-outline",
  CRLV: "car",
  "Comprovante de Residência": "home",
  default: "file-document",
};

const DOCUMENT_STATUS_OPTIONS = [
  { label: "Todos", value: null },
  { label: "Pendente", value: "Pendente" },
  { label: "Aprovado", value: "Aprovado" },
  { label: "Negado", value: "Negado" },
];

export default function AdminUserDocuments({ navigation, route }) {
  const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);
  const filterUserId = route?.params?.userId || null;
  const filterUserName = route?.params?.userName || null;
  
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [authToken, setAuthToken] = useState(null);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewRetryCount, setPreviewRetryCount] = useState(0);
  const previewBlobUrlRef = useRef(null);

  const isFocused = useIsFocused();

  useEffect(() => {
    loadToken();
    loadDocumentTypes();
  }, []);

  useEffect(() => {
    setLoading({
      show: true,
      message: "Carregando documentos",
    });
  }, []);

  useEffect(() => {
    if (isFocused) {
      loadDocuments(1, true);
    }
  }, [isFocused]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadDocuments(1, true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, selectedType, selectedStatus]);

  // Carrega a imagem do documento com auth (blob URL) para o preview no modal (web não envia header em <img>)
  useEffect(() => {
    if (!showDocumentModal || !selectedDocument || !authToken) return;
    if (!isImageFile(selectedDocument.contentType)) return;

    const url = `${urlApi}${selectedDocument.url}`;
    setImageLoading(true);
    setImageLoadFailed(false);
    setPreviewBlobUrl(null);

    let cancelled = false;

    fetch(url, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 401 ? "Não autorizado" : "Falha ao carregar");
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        if (previewBlobUrlRef.current) {
          URL.revokeObjectURL(previewBlobUrlRef.current);
          previewBlobUrlRef.current = null;
        }
        const blobUrl = URL.createObjectURL(blob);
        previewBlobUrlRef.current = blobUrl;
        setPreviewBlobUrl(blobUrl);
        setImageLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setImageLoadFailed(true);
          setImageLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
      setPreviewBlobUrl(null);
    };
  }, [showDocumentModal, selectedDocument?.id, authToken, previewRetryCount]);

  const loadToken = async () => {
    const token = await SecureStorage.getItemAsync("TokenUser");
    setAuthToken(token);
  };

  const loadDocumentTypes = () => {
    getDocumentTypes()
      .then((types) => {
        setDocumentTypes(types || []);
      })
      .catch(() => {});
  };

  const loadDocuments = useCallback(
    (pageNum = 1, reset = false) => {
      if (loadingMore && !reset) return;

      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      const filters = {};
      if (search) filters.search = search;
      if (selectedType) filters.documentType = selectedType;
      if (selectedStatus) filters.status = selectedStatus;
      if (filterUserId) filters.userId = filterUserId;

      getAllUserDocuments(filters, pageNum, PAGE_SIZE)
        .then((response) => {
          const newDocs = response.items || [];
          if (reset) {
            setDocuments(newDocs);
          } else {
            setDocuments((prev) => [...prev, ...newDocs]);
          }
          setPage(pageNum);
          setHasMore(pageNum < response.totalPages);
        })
        .catch((error) => {
          const message =
            error?.message || error?.erro || "Erro ao carregar documentos";
          setShowAlert({
            type: "alert",
            title: "Erro",
            message: message,
            show: true,
            confirmText: "OK",
            pressConfirm: () => {
              setShowAlert({ ...showAlert, show: false });
            },
          });
        })
        .finally(() => {
          setLoading({ show: false, message: "" });
          setRefreshing(false);
          setLoadingMore(false);
        });
    },
    [search, selectedType, selectedStatus, filterUserId, loadingMore, showAlert]
  );

  const handleRefresh = () => {
    setRefreshing(true);
    loadDocuments(1, true);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      loadDocuments(page + 1, false);
    }
  };

  const handleDocumentPress = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
    setImageLoading(true);
    setImageLoadFailed(false);
    setPreviewBlobUrl(null);
  };

  const handleOpenDocument = async () => {
    if (!selectedDocument) return;

    const baseUrl = urlApi;
    const url = `${baseUrl}${selectedDocument.url}`;

    if (Platform.OS === "web") {
      try {
        setLoading({ show: true, message: "Abrindo documento..." });
        const response = await fetch(url, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (!response.ok) throw new Error(response.status === 401 ? "Não autorizado" : "Falha ao carregar");
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      } catch (err) {
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: err?.message === "Não autorizado" ? "Sessão expirada. Faça login novamente." : "Não foi possível abrir o documento.",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      } finally {
        setLoading({ show: false, message: "" });
      }
    } else {
      try {
        await Linking.openURL(url);
      } catch (error) {
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: "Não foi possível abrir o documento",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      }
    }
  };

  const handleApprove = () => {
    if (!selectedDocument || reviewing) return;
    setReviewing(true);
    reviewDocument(selectedDocument.id, "Aprovado")
      .then(() => {
        setDocuments((prev) =>
          prev.map((d) =>
            d.id === selectedDocument.id ? { ...d, status: "Aprovado", rejectionReason: null } : d
          )
        );
        setSelectedDocument((d) => (d ? { ...d, status: "Aprovado", rejectionReason: null } : null));
        setShowDocumentModal(false);
        setShowAlert({
          type: "info",
          title: "Sucesso",
          message: "Documento aprovado com sucesso.",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .catch((err) => {
        const msg = err?.message || err?.erro || "Erro ao aprovar documento.";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: msg,
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .finally(() => setReviewing(false));
  };

  const handleRejectPress = () => {
    setRejectReason("");
    setShowDocumentModal(false);
    setShowRejectModal(true);
  };

  const handleConfirmReject = () => {
    const reason = (rejectReason || "").trim();
    if (!reason) {
      setShowAlert({
        type: "info",
        title: "Atenção",
        message: "Informe o motivo da rejeição.",
        show: true,
        confirmText: "OK",
        pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
      });
      return;
    }
    if (!selectedDocument || reviewing) return;
    setReviewing(true);
    reviewDocument(selectedDocument.id, "Negado", reason)
      .then(() => {
        setDocuments((prev) =>
          prev.map((d) =>
            d.id === selectedDocument.id
              ? { ...d, status: "Negado", rejectionReason: reason }
              : d
          )
        );
        setSelectedDocument((d) =>
          d ? { ...d, status: "Negado", rejectionReason: reason } : null
        );
        setShowRejectModal(false);
        setShowDocumentModal(false);
        setRejectReason("");
        setShowAlert({
          type: "info",
          title: "Sucesso",
          message: "Documento reprovado.",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .catch((err) => {
        const msg = err?.message || err?.erro || "Erro ao reprovar documento.";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: msg,
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .finally(() => setReviewing(false));
  };

  const getStatusColor = (status) => {
    const s = (status || "Pendente").toLowerCase();
    if (s === "aprovado") return Colors.SUCCESS;
    if (s === "negado") return Colors.ALERT;
    return Colors.WARNING;
  };

  const getDocumentIcon = (type) => {
    return DOCUMENT_TYPE_ICONS[type] || DOCUMENT_TYPE_ICONS.default;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isImageFile = (contentType) => {
    return contentType?.startsWith("image/");
  };

  const clearFilters = () => {
    setSelectedType(null);
    setSelectedStatus(null);
    setSearch("");
  };

  const hasActiveFilters = selectedType || selectedStatus || search;

  const getStatusFilterLabel = (value) => {
    const opt = DOCUMENT_STATUS_OPTIONS.find((o) => o.value === value);
    return opt ? opt.label : "Todos";
  };

  const renderDocumentItem = ({ item }) => (
    <TouchableOpacity
      style={styles.documentItem}
      onPress={() => handleDocumentPress(item)}
      activeOpacity={0.7}
    >
      <View style={styles.iconContainer}>
        <Icon
          name={getDocumentIcon(item.documentType)}
          size={32}
          color={Colors.PRIMARY}
        />
      </View>
      <View style={styles.documentInfo}>
        <View style={styles.documentHeader}>
          <Text style={styles.documentType} numberOfLines={1}>
            {item.documentType}
          </Text>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusBadgeText}>
              {item.status || "Pendente"}
            </Text>
          </View>
          <View style={styles.typeBadge}>
            <Text style={styles.typeBadgeText}>
              {isImageFile(item.contentType) ? "Imagem" : "PDF"}
            </Text>
          </View>
        </View>
        <Text style={styles.userName} numberOfLines={1}>
          {item.userName}
        </Text>
        <Text style={styles.userEmail} numberOfLines={1}>
          {item.userEmail}
        </Text>
        <Text style={styles.documentDate}>
          {formatDate(item.dtCreated)}
        </Text>
      </View>
      <Icon name="chevron-right" size={24} color={Colors.GRAY_DARK} />
    </TouchableOpacity>
  );

  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <Icon name="file-document-outline" size={128} color={Colors.BLACK} />
      <Text style={styles.emptyText}>
        {search || selectedType || selectedStatus
          ? "Nenhum documento encontrado para os filtros aplicados."
          : "Nenhum documento cadastrado."}
      </Text>
    </View>
  );

  const renderFooter = () => {
    if (loadingMore) {
      return (
        <View style={styles.footerLoader}>
          <Text style={styles.loadingText}>Carregando mais...</Text>
        </View>
      );
    }

    if (hasMore && documents.length > 0) {
      return (
        <TouchableOpacity style={styles.loadMoreButton} onPress={handleLoadMore}>
          <Text style={styles.loadMoreText}>Carregar mais</Text>
        </TouchableOpacity>
      );
    }

    return null;
  };

  const renderFilterSection = () => (
    <View style={styles.filterSection}>
      <TouchableOpacity
        style={styles.filterToggle}
        onPress={() => setShowFilters(!showFilters)}
      >
        <Icon name="filter-variant" size={20} color={Colors.PRIMARY} />
        <Text style={styles.filterToggleText}>Filtros</Text>
        {hasActiveFilters && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>
              {(selectedType ? 1 : 0) + (selectedStatus ? 1 : 0) + (search ? 1 : 0)}
            </Text>
          </View>
        )}
        <Icon
          name={showFilters ? "chevron-up" : "chevron-down"}
          size={20}
          color={Colors.GRAY_DARK}
        />
      </TouchableOpacity>

      {showFilters && (
        <View style={styles.filtersContainer}>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowTypeModal(true)}
          >
            <Icon name="file-document" size={18} color={Colors.GRAY_DARK} />
            <Text style={styles.filterButtonText}>
              {selectedType || "Tipo de Documento"}
            </Text>
            <Icon name="chevron-down" size={18} color={Colors.GRAY_DARK} />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowStatusModal(true)}
          >
            <Icon name="clipboard-check" size={18} color={Colors.GRAY_DARK} />
            <Text style={styles.filterButtonText}>
              {getStatusFilterLabel(selectedStatus)}
            </Text>
            <Icon name="chevron-down" size={18} color={Colors.GRAY_DARK} />
          </TouchableOpacity>

          {hasActiveFilters && (
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={clearFilters}
            >
              <Icon name="close-circle" size={16} color={Colors.ALERT} />
              <Text style={styles.clearFiltersText}>Limpar filtros</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );

  const renderTypeModal = () => (
    <Modal
      visible={showTypeModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowTypeModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Tipo de Documento</Text>
            <TouchableOpacity onPress={() => setShowTypeModal(false)}>
              <Icon name="close" size={24} color={Colors.BLACK} />
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={[
              styles.modalOption,
              !selectedType && styles.modalOptionSelected,
            ]}
            onPress={() => {
              setSelectedType(null);
              setShowTypeModal(false);
            }}
          >
            <Text
              style={[
                styles.modalOptionText,
                !selectedType && styles.modalOptionTextSelected,
              ]}
            >
              Todos
            </Text>
          </TouchableOpacity>
          {documentTypes.map((type) => (
            <TouchableOpacity
              key={type}
              style={[
                styles.modalOption,
                selectedType === type && styles.modalOptionSelected,
              ]}
              onPress={() => {
                setSelectedType(type);
                setShowTypeModal(false);
              }}
            >
              <Text
                style={[
                  styles.modalOptionText,
                  selectedType === type && styles.modalOptionTextSelected,
                ]}
              >
                {type}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Modal>
  );

  const renderStatusModal = () => (
    <Modal
      visible={showStatusModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowStatusModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Status do Documento</Text>
            <TouchableOpacity onPress={() => setShowStatusModal(false)}>
              <Icon name="close" size={24} color={Colors.BLACK} />
            </TouchableOpacity>
          </View>
          {DOCUMENT_STATUS_OPTIONS.map((opt) => (
            <TouchableOpacity
              key={opt.value ?? "all"}
              style={[
                styles.modalOption,
                selectedStatus === opt.value && styles.modalOptionSelected,
              ]}
              onPress={() => {
                setSelectedStatus(opt.value);
                setShowStatusModal(false);
              }}
            >
              <Text
                style={[
                  styles.modalOptionText,
                  selectedStatus === opt.value && styles.modalOptionTextSelected,
                ]}
              >
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Modal>
  );

  const renderDocumentModal = () => {
    if (!selectedDocument) return null;

    return (
      <Modal
        visible={showDocumentModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDocumentModal(false)}
      >
        <View style={styles.documentModalOverlay}>
          <View style={styles.documentModalContent}>
            <View style={styles.documentModalHeader}>
              <View>
                <Text style={styles.documentModalTitle}>
                  {selectedDocument.documentType}
                </Text>
                <Text style={styles.documentModalSubtitle}>
                  {selectedDocument.userName}
                </Text>
                <View style={[styles.statusBadgeLarge, { backgroundColor: getStatusColor(selectedDocument.status) }]}>
                  <Text style={styles.statusBadgeText}>
                    {selectedDocument.status || "Pendente"}
                  </Text>
                </View>
                {selectedDocument.rejectionReason ? (
                  <View style={styles.rejectionReasonBox}>
                    <Text style={styles.rejectionReasonLabel}>Motivo da rejeição:</Text>
                    <Text style={styles.rejectionReasonText}>{selectedDocument.rejectionReason}</Text>
                  </View>
                ) : null}
              </View>
              <TouchableOpacity onPress={() => setShowDocumentModal(false)}>
                <Icon name="close" size={24} color={Colors.BLACK} />
              </TouchableOpacity>
            </View>

            <View style={styles.documentModalBody}>
              {isImageFile(selectedDocument.contentType) ? (
                <View style={styles.imageContainer}>
                  {imageLoadFailed ? (
                    <View style={styles.imageErrorContainer}>
                      <Icon name="image-broken-variant" size={64} color={Colors.GRAY_DARK} />
                      <Text style={styles.imageErrorText}>Não foi possível carregar a imagem</Text>
                      <Text style={styles.imageErrorSubtext}>Use "Abrir em nova aba" para visualizar</Text>
                      <TouchableOpacity
                        style={styles.imageRetryButton}
                        onPress={() => {
                          setImageLoadFailed(false);
                          setPreviewRetryCount((c) => c + 1);
                        }}
                      >
                        <Icon name="refresh" size={20} color={Colors.WHITE} />
                        <Text style={styles.imageRetryText}>Tentar novamente</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <>
                      {imageLoading && (
                        <View style={styles.imageLoading}>
                          <Icon name="loading" size={48} color={Colors.PRIMARY} />
                          <Text style={styles.imageLoadingText}>Carregando...</Text>
                        </View>
                      )}
                      {previewBlobUrl && (
                        <Image
                          source={{ uri: previewBlobUrl }}
                          style={styles.documentImage}
                          resizeMode="contain"
                        />
                      )}
                    </>
                  )}
                </View>
              ) : (
                <View style={styles.pdfContainer}>
                  <Icon name="file-pdf-box" size={80} color={Colors.ALERT} />
                  <Text style={styles.pdfText}>Documento PDF</Text>
                  <Text style={styles.pdfFileName}>{selectedDocument.fileName}</Text>
                </View>
              )}
            </View>

            <View style={styles.documentModalInfo}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Usuário:</Text>
                <Text style={styles.infoValue}>{selectedDocument.userName}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Email:</Text>
                <Text style={styles.infoValue}>
                  {selectedDocument.userEmailRaw || selectedDocument.userEmail}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>CPF/CNPJ:</Text>
                <Text style={styles.infoValue}>
                  {selectedDocument.userDocumentRaw || selectedDocument.userDocument}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Data:</Text>
                <Text style={styles.infoValue}>
                  {formatDate(selectedDocument.dtCreated)}
                </Text>
              </View>
            </View>

            <View style={styles.documentModalActions}>
              <Button
                textoBotao="Abrir em nova aba"
                onPress={handleOpenDocument}
                estilo={styles.openButton}
              />
              {(selectedDocument.status || "Pendente").toLowerCase() !== "aprovado" && (
                <TouchableOpacity
                  style={[styles.reviewButton, styles.approveButton]}
                  onPress={handleApprove}
                  disabled={reviewing}
                >
                  {reviewing ? (
                    <ActivityIndicator size="small" color={Colors.WHITE} />
                  ) : (
                    <>
                      <Icon name="check-circle" size={20} color={Colors.WHITE} />
                      <Text style={styles.reviewButtonText}>Aprovar</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}
              {(selectedDocument.status || "Pendente").toLowerCase() !== "negado" && (
                <TouchableOpacity
                  style={[styles.reviewButton, styles.rejectButton]}
                  onPress={handleRejectPress}
                  disabled={reviewing}
                >
                  <Icon name="close-circle" size={20} color={Colors.WHITE} />
                  <Text style={styles.reviewButtonText}>Reprovar</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const pageTitle = filterUserName
    ? `Documentos de ${filterUserName}`
    : "Documentos de Usuários";

  return (
    <PageTitle title={pageTitle} navigation={navigation}>
      <View style={styles.container}>
        <TextBoxIcon
          iconName="magnify"
          placeholderInput="Buscar por nome, email ou tipo"
          value={search}
          onChangeTextInput={setSearch}
          styleBox={styles.searchBox}
        />
        
        {!filterUserId && renderFilterSection()}
        
        <FlatList
          data={documents}
          renderItem={renderDocumentItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={documents.length === 0 ? styles.emptyList : null}
          ListEmptyComponent={renderEmptyList}
          ListFooterComponent={renderFooter}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[Colors.PRIMARY]}
              tintColor={Colors.PRIMARY}
            />
          }
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      </View>

      {renderTypeModal()}
      {renderStatusModal()}
      {renderDocumentModal()}

      {/* Modal motivo da rejeição */}
      <Modal
        visible={showRejectModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (!reviewing) {
            setShowRejectModal(false);
            setShowDocumentModal(true);
          }
        }}
      >
        <View style={styles.rejectModalOverlay}>
          <View style={styles.rejectModalContent}>
            <Text style={styles.rejectModalTitle}>Motivo da rejeição</Text>
            <Text style={styles.rejectModalSubtitle}>
              Informe o motivo pelo qual o documento está sendo reprovado (obrigatório).
            </Text>
            <TextInput
              style={styles.rejectReasonInput}
              placeholder="Ex.: Documento ilegível, dados inconsistentes..."
              placeholderTextColor={Colors.GRAY_DARK}
              value={rejectReason}
              onChangeText={setRejectReason}
              multiline
              numberOfLines={3}
              editable={!reviewing}
            />
            <View style={styles.rejectModalActions}>
              <TouchableOpacity
                style={styles.rejectModalCancelBtn}
                onPress={() => {
                  if (!reviewing) {
                    setShowRejectModal(false);
                    setShowDocumentModal(true);
                  }
                }}
                disabled={reviewing}
              >
                <Text style={styles.rejectModalCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.rejectModalConfirmBtn, (!rejectReason.trim() || reviewing) && styles.rejectModalConfirmDisabled]}
                onPress={handleConfirmReject}
                disabled={!rejectReason.trim() || reviewing}
              >
                {reviewing ? (
                  <ActivityIndicator size="small" color={Colors.WHITE} />
                ) : (
                  <Text style={styles.rejectModalConfirmText}>Confirmar rejeição</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageTitle>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.WHITE,
  },
  searchBox: {
    marginHorizontal: 8,
    marginTop: 8,
  },
  filterSection: {
    marginHorizontal: 8,
    marginTop: 8,
  },
  filterToggle: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    backgroundColor: Colors.SNOW,
    borderRadius: 8,
  },
  filterToggleText: {
    flex: 1,
    marginLeft: 8,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },
  filterBadge: {
    backgroundColor: Colors.PRIMARY,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginRight: 8,
  },
  filterBadgeText: {
    color: Colors.WHITE,
    fontSize: Fonts.FONT_SIZE_12,
    fontWeight: "bold",
  },
  filtersContainer: {
    marginTop: 8,
    padding: 12,
    backgroundColor: Colors.SNOW,
    borderRadius: 8,
  },
  filterButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    backgroundColor: Colors.WHITE,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.GRAY_LIGHT,
  },
  filterButtonText: {
    flex: 1,
    marginLeft: 8,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  clearFiltersButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 12,
    padding: 8,
  },
  clearFiltersText: {
    marginLeft: 4,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.ALERT,
  },
  documentItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: Colors.WHITE,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: Colors.SNOW,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  documentInfo: {
    flex: 1,
  },
  documentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  documentType: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
    flex: 1,
    marginRight: 8,
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    backgroundColor: Colors.PRIMARY,
  },
  typeBadgeText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    marginRight: 8,
  },
  statusBadgeText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  userName: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
    marginBottom: 2,
  },
  userEmail: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 2,
  },
  documentDate: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.GRAY_LIGHT,
    marginHorizontal: 16,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    paddingVertical: 48,
  },
  emptyText: {
    marginTop: 24,
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    textAlign: "center",
    paddingHorizontal: 32,
  },
  emptyList: {
    flexGrow: 1,
  },
  footerLoader: {
    padding: 16,
    alignItems: "center",
  },
  loadingText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  loadMoreButton: {
    padding: 16,
    alignItems: "center",
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.PRIMARY,
  },
  loadMoreText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "flex-end",
  },
  modalContent: {
    backgroundColor: Colors.WHITE,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 32,
    maxHeight: "70%",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  modalTitle: {
    fontSize: Fonts.FONT_SIZE_18,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  modalOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  modalOptionSelected: {
    backgroundColor: Colors.SNOW,
  },
  modalOptionText: {
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
  },
  modalOptionTextSelected: {
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },
  documentModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  documentModalContent: {
    backgroundColor: Colors.WHITE,
    borderRadius: 16,
    width: "100%",
    maxWidth: 600,
    maxHeight: "90%",
  },
  documentModalHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  documentModalTitle: {
    fontSize: Fonts.FONT_SIZE_18,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  documentModalSubtitle: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginTop: 4,
  },
  documentModalBody: {
    minHeight: 200,
    maxHeight: 400,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.SNOW,
  },
  imageContainer: {
    width: "100%",
    height: 300,
    alignItems: "center",
    justifyContent: "center",
  },
  imageLoading: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
  },
  imageLoadingText: {
    marginTop: 8,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  imageErrorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  imageErrorText: {
    marginTop: 16,
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "600",
    color: Colors.BLACK,
    textAlign: "center",
  },
  imageErrorSubtext: {
    marginTop: 8,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    textAlign: "center",
  },
  imageRetryButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.PRIMARY,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 16,
    gap: 8,
  },
  imageRetryText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  documentImage: {
    width: "100%",
    height: "100%",
  },
  pdfContainer: {
    padding: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  pdfText: {
    marginTop: 16,
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  pdfFileName: {
    marginTop: 8,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    textAlign: "center",
  },
  documentModalInfo: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.GRAY_LIGHT,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    width: 80,
  },
  infoValue: {
    flex: 1,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
  },
  documentModalActions: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.GRAY_LIGHT,
    gap: 8,
  },
  openButton: {
    backgroundColor: Colors.PRIMARY,
  },
  statusBadgeLarge: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 8,
  },
  rejectionReasonBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: Colors.SNOW,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: Colors.ALERT,
  },
  rejectionReasonLabel: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
    fontWeight: "bold",
    marginBottom: 4,
  },
  rejectionReasonText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
  },
  reviewButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  reviewButtonText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  approveButton: {
    backgroundColor: Colors.SUCCESS,
  },
  rejectButton: {
    backgroundColor: Colors.ALERT,
  },
  rejectModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  rejectModalContent: {
    backgroundColor: Colors.WHITE,
    borderRadius: 16,
    padding: 20,
    width: "100%",
    maxWidth: 400,
  },
  rejectModalTitle: {
    fontSize: Fonts.FONT_SIZE_18,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 8,
  },
  rejectModalSubtitle: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 16,
  },
  rejectReasonInput: {
    borderWidth: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderRadius: 8,
    padding: 12,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
    minHeight: 80,
    textAlignVertical: "top",
    marginBottom: 16,
  },
  rejectModalActions: {
    flexDirection: "row",
    gap: 12,
  },
  rejectModalCancelBtn: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.GRAY_DARK,
    alignItems: "center",
  },
  rejectModalCancelText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    fontWeight: "600",
  },
  rejectModalConfirmBtn: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    backgroundColor: Colors.ALERT,
    alignItems: "center",
    justifyContent: "center",
  },
  rejectModalConfirmDisabled: {
    opacity: 0.6,
  },
  rejectModalConfirmText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
});
